import { AnonymousData } from '../decorators/mapped'
import { MorphLink } from '../types/morph-link'
import { Serialisable, SerialisationConfig } from './serialisable'

export class Morphable {
  constructor(
    public readonly type: string,
    public readonly data: unknown
  ) {}

  attr(key: string) {
    return (this.data as AnonymousData)[key]
  }
}

export function mapMorphable(data: unknown): Morphable | undefined {
  if (!data) {
    return undefined
  }
  if (
    Object.prototype.hasOwnProperty.call(data, 'type') &&
    Object.prototype.hasOwnProperty.call(data, 'data')
  ) {
    return new Morphable(
      (data as AnonymousData)['type'] as string,
      (data as AnonymousData)['data']
    )
  }
  if (!(data as AnonymousData)['morph_type']) {
    return undefined
  }
  return new Morphable((data as AnonymousData)['morph_type'] as string, data)
}

export function mapLegacyMorphable(data: unknown): Morphable | undefined {
  if (!data) {
    return undefined
  }
  return new Morphable(
    (data as AnonymousData)['type'] as string,
    (data as AnonymousData)['data']
  )
}

export function morphableName(morphable: Morphable | undefined): string {
  if (morphable) {
    if (['engineer', 'user'].includes(morphable.type)) {
      return `${morphable.attr('first_name')} ${morphable.attr('last_name')}`
    }
    if (morphable.type === 'site') {
      return `${morphable.attr('name')}`
    }
  }

  return ''
}

export function morphableNames(morphable: Morphable | undefined): string[] {
  if (morphable) {
    if (['engineer', 'user'].includes(morphable.type)) {
      return [
        `${morphable.attr('first_name')}`,
        `${morphable.attr('last_name')}`,
      ]
    }
    if (morphable.type === 'site') {
      return [`${morphable.attr('name')}`]
    }
  }
  return []
}

export function morphableType(
  morphable: Morphable | undefined
): string | undefined {
  if (morphable) {
    if (morphable.type === 'engineer') {
      return 'Engineer'
    }
    if (morphable.type === 'site') {
      return 'Site'
    }
    if (morphable.type === 'user') {
      return 'User'
    }
  }

  return undefined
}

export function morphableLink(
  morphable: Morphable | undefined
): MorphLink | undefined {
  if (morphable) {
    return { type: morphable.type, id: morphable.attr('numeric_id') as number }
  }
  return undefined
}

export function morphableLinkID(
  morphable: Morphable | undefined
): string | undefined {
  const link = morphableLink(morphable)
  return link ? `${link.type}-${link.id}` : undefined
}

export function morphableSortKey(
  morphable: Morphable | undefined
): string | undefined {
  return morphable
    ? `${morphableType(morphable)} - ${morphableNames(morphable).reverse().join(' - ')}`
    : undefined
}

export class SerialisedMorphable extends Serialisable {
  public static override serialisationConfig: SerialisationConfig =
    new SerialisationConfig(() => new SerialisedMorphable())

  morphable?: Morphable

  override update(attrs: unknown, _config: SerialisationConfig) {
    this.morphable = mapLegacyMorphable(attrs)
    return this
  }

  get name(): string {
    return morphableName(this.morphable)
  }
}
